/**
 * Catálogo curado de especialidades gastronómicas locais.
 *
 * REGRAS DE INTEGRIDADE (ver plano "Ramo Premium isolado: especialidade
 * gastronómica local"):
 * - cada associação produto → estabelecimento guarda `source`, `verifiedAt`,
 *   `countryCode` e, quando disponível, `provider` + `providerPlaceId`;
 * - `venueName`/`aliases` servem apenas para pesquisa e apresentação, NUNCA
 *   como prova de ligação;
 * - a correspondência usa primeiro provider + placeId + país; coordenadas são
 *   fallback. Nome parecido nunca liga um estabelecimento.
 *
 * O catálogo é OPCIONAL: a ausência de entrada para uma cidade/país nunca
 * bloqueia o motor — só torna as etiquetas de produto mais conservadoras.
 */

export type CuratedVenueLink = {
  /** Nome apenas para pesquisa/apresentação. Nunca é prova. */
  venueName: string;
  aliases?: string[];
  provider?: "google";
  providerPlaceId?: string;
  lat?: number;
  lng?: number;
  countryCode: string;
  source: string;
  /** ISO date da última verificação humana. */
  verifiedAt: string;
};

export type CuratedSpecialty = {
  id: string;
  /** Nome do produto tal como é mostrado ao utilizador. */
  product: string;
  /** Sinónimos de pesquisa/apresentação (nunca prova). */
  aliases: string[];
  city: string;
  countryCode: string;
  /** Foco do motor a que a especialidade pertence. */
  focus: "sweets" | "restaurant";
  /**
   * true → o termo designa um alvo/estabelecimento específico
   * ("Pastéis de Belém"), não um produto genérico ("pastel de nata").
   */
  specificTarget?: boolean;
  /** Termos que, encontrados nos dados reais do local, provam o produto. */
  evidenceTerms: string[];
  venues: CuratedVenueLink[];
};

export const LOCAL_SPECIALTY_CATALOG: CuratedSpecialty[] = [
  {
    id: "pt-viana-bolas-natario",
    product: "Bolas de Berlim do Natário",
    aliases: ["bolas de berlim do natario", "bola de berlim do natario", "natario"],
    city: "Viana do Castelo",
    countryCode: "PT",
    focus: "sweets",
    specificTarget: true,
    evidenceTerms: ["natario", "bola de berlim", "bolas de berlim"],
    venues: [
      {
        venueName: "Confeitaria Natário",
        aliases: ["natário", "natario"],
        lat: 41.6932,
        lng: -8.8302,
        countryCode: "PT",
        source: "curadoria Routify (verificação manual do estabelecimento)",
        verifiedAt: "2026-08-22",
      },
    ],
  },
  {
    id: "pt-viana-torta",
    product: "Torta de Viana",
    aliases: ["torta de viana", "tortas de viana"],
    city: "Viana do Castelo",
    countryCode: "PT",
    focus: "sweets",
    evidenceTerms: ["torta de viana"],
    venues: [],
  },
  {
    id: "pt-lisboa-pastel-nata",
    product: "Pastel de nata",
    aliases: ["pastel de nata", "pasteis de nata", "pastéis de nata"],
    city: "Lisboa",
    countryCode: "PT",
    focus: "sweets",
    evidenceTerms: ["pastel de nata", "pasteis de nata"],
    venues: [],
  },
  {
    // Alvo específico — NÃO é sinónimo de "pastel de nata".
    id: "pt-lisboa-pasteis-belem",
    product: "Pastéis de Belém",
    aliases: ["pasteis de belem", "pastéis de belém", "pastel de belem"],
    city: "Lisboa",
    countryCode: "PT",
    focus: "sweets",
    specificTarget: true,
    evidenceTerms: ["pasteis de belem", "fabrica dos pasteis de belem"],
    venues: [
      {
        venueName: "Pastéis de Belém",
        aliases: ["fábrica dos pastéis de belém"],
        lat: 38.6975,
        lng: -9.2033,
        countryCode: "PT",
        source: "curadoria Routify (verificação manual do estabelecimento)",
        verifiedAt: "2026-08-22",
      },
    ],
  },
  {
    id: "pt-aveiro-ovos-moles",
    product: "Ovos Moles de Aveiro",
    aliases: ["ovos moles", "ovos moles de aveiro"],
    city: "Aveiro",
    countryCode: "PT",
    focus: "sweets",
    evidenceTerms: ["ovos moles"],
    venues: [],
  },
  {
    id: "pt-sintra-travesseiros",
    product: "Travesseiros e queijadas de Sintra",
    aliases: ["travesseiros", "queijadas", "travesseiro de sintra"],
    city: "Sintra",
    countryCode: "PT",
    focus: "sweets",
    evidenceTerms: ["travesseiro", "queijada"],
    venues: [],
  },
  {
    id: "pt-porto-francesinha",
    product: "Francesinha",
    aliases: ["francesinha", "francesinhas"],
    city: "Porto",
    countryCode: "PT",
    focus: "restaurant",
    evidenceTerms: ["francesinha"],
    venues: [],
  },
];

export const foldSpecialty = (value: string): string =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .replace(/\s{2,}/gu, " ")
    .toLowerCase()
    .trim();

/** Entradas curadas para uma cidade (e país, quando conhecido). */
export function catalogEntriesForCity(
  city: string | null | undefined,
  countryCode?: string | null,
): CuratedSpecialty[] {
  const target = foldSpecialty(city ?? "");
  if (!target) return [];
  const cc = (countryCode ?? "").toUpperCase();
  return LOCAL_SPECIALTY_CATALOG.filter((entry) => {
    if (foldSpecialty(entry.city) !== target) return false;
    return cc ? entry.countryCode === cc : true;
  });
}

/**
 * Procura no catálogo o produto nomeado no texto do pedido. Os alvos
 * específicos ("Pastéis de Belém") têm prioridade sobre produtos genéricos
 * com nome parecido ("pastel de nata").
 */
export function catalogSpecialtyForTerm(
  text: string,
  countryCode?: string | null,
): CuratedSpecialty | null {
  const folded = foldSpecialty(text ?? "");
  if (!folded) return null;
  const cc = (countryCode ?? "").toUpperCase();
  const scoped = cc
    ? LOCAL_SPECIALTY_CATALOG.filter((e) => e.countryCode === cc)
    : LOCAL_SPECIALTY_CATALOG;
  const pool = scoped.length ? scoped : LOCAL_SPECIALTY_CATALOG;
  const matches = pool.filter((entry) =>
    [entry.product, ...entry.aliases].some((term) => folded.includes(foldSpecialty(term))),
  );
  if (!matches.length) return null;
  const specific = matches.filter((m) => m.specificTarget);
  const best = (specific.length ? specific : matches).sort(
    (a, b) => longestAliasLength(b) - longestAliasLength(a),
  );
  return best[0] ?? null;
}

function longestAliasLength(entry: CuratedSpecialty): number {
  return [entry.product, ...entry.aliases].reduce(
    (max, term) => Math.max(max, foldSpecialty(term).length),
    0,
  );
}

export type CuratedVenueMatchInput = {
  placeId?: string | null;
  lat?: number | null;
  lng?: number | null;
  countryCode?: string | null;
};

function metersBetween(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000;
  const toRad = (v: number) => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/**
 * Correspondência de um estabelecimento curado.
 * Ordem: provider + providerPlaceId + país → coordenadas (fallback, ≤200 m,
 * país igual). Nome parecido NUNCA liga.
 */
export function matchesCuratedVenue(
  link: CuratedVenueLink,
  stop: CuratedVenueMatchInput,
): boolean {
  const stopCountry = (stop.countryCode ?? "").toUpperCase();
  if (stopCountry && stopCountry !== link.countryCode.toUpperCase()) return false;
  if (link.provider && link.providerPlaceId) {
    if (!stop.placeId) {
      // Sem identificador não há prova por provider; passa ao fallback.
    } else {
      return stop.placeId === link.providerPlaceId;
    }
  }
  if (
    typeof link.lat === "number" &&
    typeof link.lng === "number" &&
    typeof stop.lat === "number" &&
    typeof stop.lng === "number"
  ) {
    // Sem país conhecido no local, coordenadas próximas continuam a exigir
    // proximidade real ao estabelecimento curado.
    return (
      metersBetween({ lat: stop.lat, lng: stop.lng }, { lat: link.lat, lng: link.lng }) <= 200
    );
  }
  return false;
}
